// deps
import { useContext, useRef } from "react";

// hooks
import { useEffectOnce, useMountedRef } from "@swiggy-private/core-ui";

// helpers
import { clearWebviewRefreshSavedState } from "@payment-client/helpers/Misc";
import { PollingEnabledPaymentFlow } from "@payment-client/includes/PaymentFlow";

// interfaces
import { CheckoutActionTypes, CheckoutContext, ApiContext, PublicApiContext } from "@swiggy-private/checkout-core";

interface PollingTxnStatusProps {
    pollingIntervalInMs: number;
    pollingDurationInMs: number;
    startTime?: number;
    paymentCode: string;
    removeCustomBackHandling?: () => void;
}

const usePollingTxnStatus = (props: PollingTxnStatusProps): VoidFunction => {
    const isMountedRef = useMountedRef();
    const apiContextValue = useContext(ApiContext);
    const publicApiConfig = useContext(PublicApiContext);
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const txnStatusRef = useRef(false);
    const paymentFlowRef = useRef<PollingEnabledPaymentFlow | null>(null);

    useEffectOnce(() => {
        if (
            (checkoutState.type !== CheckoutActionTypes.VPA_TIMER &&
                checkoutState.type !== CheckoutActionTypes.PAYMENT_TXN_STATUS_POLLER) ||
            apiContextValue.host.isNone()
        ) {
            return;
        }

        const orderData = checkoutState.payload.order;
        const apiHost = apiContextValue.host.unwrap();
        const paymentFlow = new PollingEnabledPaymentFlow(apiHost, orderData, publicApiConfig || {});
        paymentFlowRef.current = paymentFlow;

        const moveToNextState = (): void => {
            if (checkoutState.type === CheckoutActionTypes.VPA_TIMER) {
                clearWebviewRefreshSavedState();
                setCheckoutState({
                    type: CheckoutActionTypes.PROCESS_ORDER,
                    payload: {
                        placeOrderData: checkoutState.payload.placeOrderData,
                        order: checkoutState.payload.order,
                    },
                });
            }

            if (checkoutState.type === CheckoutActionTypes.PAYMENT_TXN_STATUS_POLLER) {
                props.removeCustomBackHandling?.();
                setCheckoutState({
                    type: CheckoutActionTypes.CONFIRM_ORDER,
                    payload: {
                        orderId: String(checkoutState.payload.order.orderId),
                        order: checkoutState.payload.order,
                        placeOrderData: checkoutState.payload.placeOrderData,
                    },
                });
            }
        };

        // start the payment flow..
        paymentFlow
            .checkTxnStatus({
                duration: props.pollingDurationInMs,
                delay: props.pollingIntervalInMs,
                startTime: props.startTime,
                paymentCode: props.paymentCode,
            })
            .then((_result) => {
                if (!isMountedRef.current || !paymentFlow.isRunning()) {
                    return;
                }

                txnStatusRef.current = true;

                if (document.visibilityState == "hidden" || document.hidden) {
                    paymentFlow.confirmOrder();
                    window.requestAnimationFrame(moveToNextState);
                } else {
                    paymentFlow.stop();
                    moveToNextState();
                }
            });

        return (): void => {
            if (!txnStatusRef.current) {
                if (checkoutState.type === CheckoutActionTypes.VPA_TIMER) {
                    clearWebviewRefreshSavedState();
                }
                paymentFlow.stop();
            }
        };
    });

    return (): void => {
        props.removeCustomBackHandling?.();
        paymentFlowRef.current?.stop();
    };
};

export default usePollingTxnStatus;
